import React from 'react';
import {Bar, BarChart, ResponsiveContainer, XAxis, YAxis} from 'recharts';

import {TEAL_500} from '../../../../common/css/color.styles';
import {
  formatNumber,
  SHARED_AXIS_PROPS,
  useHoverableBars,
} from './numberListChartCommon';
import {computeBins} from './numberListStats';

type NumberListHistogramProps = {
  values: number[];
  /** Bar fill (e.g. the eval's color); defaults to teal. */
  color?: string;
};

// A bin plus its display strings: an axis label and a tooltip range.
type HistogramBar = {label: string; range: string; count: number};

/**
 * Histogram of a numeric list: value bins on the X axis, frequency on the Y
 * axis. Bin math lives in numberListStats.computeBins; this only maps bins to
 * display labels and renders. Fills its parent, so the parent must have a
 * definite size.
 */
export const NumberListHistogram = ({
  values,
  color,
}: NumberListHistogramProps) => {
  const fill = color ?? TEAL_500;
  const {barShape, overlay} = useHoverableBars({
    fill,
    labelOf: (d: HistogramBar) => `${d.range}: ${d.count}`,
  });

  if (values.length === 0) {
    return null;
  }

  const bins = computeBins(values);
  const data: HistogramBar[] = bins.map((b, i) => ({
    label: formatNumber(b.x0),
    range:
      b.x0 === b.x1
        ? `= ${formatNumber(b.x0)}`
        : `[${formatNumber(b.x0)}, ${formatNumber(b.x1)}${
            i === bins.length - 1 ? ']' : ')'
          }`,
    count: b.count,
  }));

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{top: 2, right: 8, bottom: 0, left: 0}}
          barCategoryGap={1}>
          <XAxis dataKey="label" height={14} {...SHARED_AXIS_PROPS} />
          <YAxis
            type="number"
            allowDecimals={false}
            width={20}
            {...SHARED_AXIS_PROPS}
          />
          <Bar
            dataKey="count"
            fill={fill}
            isAnimationActive={false}
            radius={[2, 2, 0, 0]}
            shape={barShape}
          />
        </BarChart>
      </ResponsiveContainer>
      {overlay}
    </>
  );
};
